import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GameId, LaunchStatus } from '@mental-mint/shared-types';
import { Player } from '../Model/CommonModel';

// Minimal Game type to avoid circular dependencies with GameSettings
// Each game app will provide their own full GameSettings implementation
export interface Game {
  metadata: {
    gameId: string;
    gameName: string;
    gameDescription: string;
    gameURL: string;
    launchStatus: LaunchStatus;
  };
  colorPrimary: string;
  colorSecondary?: string;
  fontPrimary: string;
  fontSecondary?: string;
  howToPlayGif?: string;
  [key: string]: any; // Allow any additional properties
}

/**
 * Generic event for inter-page communication within a game.
 * Pages can emit events that other pages listen to, without tight coupling.
 */
export interface GameEvent {
  type: string;
  payload?: any;
  timestamp: number;
  gameId?: string;
}

interface GameSessionState {
  currentGameId: GameId | null;
  currentGame: Game | null;
  player: Player | null;
  isInitialized: boolean;
  gameEvents: GameEvent[];
}

const initialState: GameSessionState = {
  currentGameId: null,
  currentGame: null,
  player: null,
  isInitialized: false,
  gameEvents: [],
};

const gameSessionSlice = createSlice({
  name: 'gameSession',
  initialState,  
  reducers: {
    initializeSession: (state, action: PayloadAction<{ gameId: GameId; game: Game; player: Player }>) => {
      console.log(`Initializing game session for game: "${action.payload.gameId}" and playerId: "${action.payload.player.playerId}".`);
      state.currentGameId = action.payload.gameId;
      state.currentGame = action.payload.game;
      state.player = action.payload.player;
      state.isInitialized = true;
    },
    // Could be useful one day for login/out systems.
    updatePlayer: (state, action: PayloadAction<Player>) => {
      state.player = action.payload;
    },
    // Could be useful one day for switching games.
    clearSession: (state) => {
      state.currentGameId = null;
      state.currentGame = null;
      state.player = null;
      state.isInitialized = false;
      state.gameEvents = [];
    },
    /**
     * Emit a game event for inter-page communication.
     * Other pages can listen for this event type and react accordingly.
     */
    emitGameEvent: (state, action: PayloadAction<{ type: string; payload?: any; gameId?: string }>) => {
      state.gameEvents.push({
        type: action.payload.type,
        payload: action.payload.payload,
        gameId: action.payload.gameId,
        timestamp: Date.now(),
      });
    },
    /**
     * Clear a specific game event by timestamp after it's been handled.
     */
    clearGameEvent: (state, action: PayloadAction<number>) => {
      state.gameEvents = state.gameEvents.filter(event => event.timestamp !== action.payload);
    },
    /**
     * Clear all game events of a specific type.
     */
    clearGameEventsByType: (state, action: PayloadAction<string>) => {
      state.gameEvents = state.gameEvents.filter(event => event.type !== action.payload);
    },
    /**
     * Clear all game events (useful when navigating away or starting new game).
     */
    clearAllGameEvents: (state) => {
      state.gameEvents = [];
    },
  },
});

export const { initializeSession, updatePlayer, clearSession, emitGameEvent, clearGameEvent, clearGameEventsByType, clearAllGameEvents } = gameSessionSlice.actions;

export default gameSessionSlice.reducer;
